// src/components/Login.jsx

import React, {
  useEffect,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import Swal from "sweetalert2";

import "./login.css";


import { API_BASE } from "../apiConfig";

/* ============================================================
   CLEAR AUTH DATA
============================================================ */

const clearLoginData = () => {

  localStorage.removeItem("auth");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("tokenExpiresAt");
  localStorage.removeItem("user");

  localStorage.removeItem("userId");
  localStorage.removeItem("userName");
  localStorage.removeItem("username");
  localStorage.removeItem("userRole");

  localStorage.removeItem("userPhone");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("profilePicture");

  localStorage.removeItem("loginTime");

};


export default function Login() {

  const navigate = useNavigate();


  /* ============================================================
     API
  ============================================================ */

  const API_URL =
    `${API_BASE}login.php`;


  /* ============================================================
     STATE
  ============================================================ */

  const [formData, setFormData] =
    useState({
      username: "",
      password: "",
    });


  const [
    showPassword,
    setShowPassword,
  ] = useState(false);


  const [
    loading,
    setLoading,
  ] = useState(false);



  /* ============================================================
     EXISTING LOGIN / CROSS-TAB LOGIN SYNC

     localStorage is shared by every tab of the same site.

     IMPORTANT:
     Do NOT clear login data when Login.jsx opens.
     Otherwise refreshing an old login tab will erase the login
     that was created from another tab.

     Behaviour:
     1. If already logged in -> redirect to correct dashboard.
     2. If another tab logs in -> this login tab redirects too.
     3. If another tab logs out -> this tab remains on login page.
  ============================================================ */

  useEffect(() => {

    const redirectLoggedInUser = () => {

      const auth =
        localStorage.getItem("auth");

      if (auth !== "loggedin") {
        return false;
      }

      const role =
        String(
          localStorage.getItem("userRole") || ""
        )
          .trim()
          .toLowerCase();

      const userId =
        String(
          localStorage.getItem("userId") || ""
        ).trim();

      /*
        Do not redirect with incomplete/stale localStorage data.
      */
      if (!userId || !role) {
        return false;
      }

      if (role === "doctor") {
        navigate(
          "/doctor",
          { replace: true }
        );

        return true;
      }

      if (role === "admin") {
        navigate(
          "/dashboard",
          { replace: true }
        );

        return true;
      }

      if (
        role === "reception" ||
        role === "nursing station"
      ) {
        navigate(
          "/frontdesk",
          { replace: true }
        );

        return true;
      }

      /*
        Unknown role = remove only invalid/stale auth data.
      */
      clearLoginData();

      return false;
    };


    /*
      Check immediately when Login page opens/refreshed.
    */
    redirectLoggedInUser();


    /*
      Fires in OTHER tabs when localStorage changes.
      This makes a login in one tab automatically update
      another tab that is currently showing Login.jsx.
    */
    const handleStorage = (event) => {

      if (
        event.key === "auth" ||
        event.key === "userRole" ||
        event.key === "userId" ||
        event.key === "loginEvent"
      ) {
        redirectLoggedInUser();
      }

    };


    /*
      Same-tab custom authentication event.
    */
    const handleAuthChanged = () => {
      redirectLoggedInUser();
    };


    /*
      When user returns to this tab, check again.
    */
    const handleFocus = () => {
      redirectLoggedInUser();
    };


    window.addEventListener(
      "storage",
      handleStorage
    );

    window.addEventListener(
      "authChanged",
      handleAuthChanged
    );

    window.addEventListener(
      "focus",
      handleFocus
    );


    return () => {

      window.removeEventListener(
        "storage",
        handleStorage
      );

      window.removeEventListener(
        "authChanged",
        handleAuthChanged
      );

      window.removeEventListener(
        "focus",
        handleFocus
      );

    };

  }, [navigate]);



  /* ============================================================
     INPUT CHANGE
  ============================================================ */

  const handleChange = (e) => {

    const {
      name,
      value,
    } = e.target;


    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

  };



  /* ============================================================
     VALIDATION
  ============================================================ */

  const validateForm = () => {

    if (!formData.username.trim()) {

      Swal.fire({
        icon: "warning",
        title: "Username Required",
        text:
          "Please enter your username.",
        confirmButtonText: "OK",
      });

      return false;
    }


    if (!formData.password) {

      Swal.fire({
        icon: "warning",
        title: "Password Required",
        text:
          "Please enter your password.",
        confirmButtonText: "OK",
      });

      return false;
    }


    return true;

  };



  /* ============================================================
     LOGIN
  ============================================================ */

  const handleSubmit = async (e) => {

    e.preventDefault();


    if (!validateForm()) {
      return;
    }


    setLoading(true);


    try {

      const response =
        await fetch(
          API_URL,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              username:
                formData.username.trim(),

              password:
                formData.password,
            }),
          }
        );


      /*
        Read as text first.

        Prevents React from crashing
        if PHP sends warning/error text.
      */

      const text =
        await response.text();


      let result;


      try {

        result =
          JSON.parse(text);

      } catch (error) {

        console.error(
          "Invalid PHP response:",
          text
        );


        await Swal.fire({
          icon: "error",
          title: "Server Error",
          text:
            "Login API did not return valid JSON.",
        });


        return;

      }



      /* ========================================================
         LOGIN SUCCESS
      ======================================================== */

      if (
        result.status === "success"
      ) {

        const user =
          result.user;

        if (!result.token) {
          await Swal.fire({ icon: "error", title: "Login Failed", text: "Security token was not returned by the server." });
          return;
        }


        if (!user) {

          await Swal.fire({
            icon: "error",
            title: "Login Failed",
            text:
              "User information was not returned by the server.",
          });

          return;

        }



        /* ======================================================
           NORMALIZE ROLE
        ====================================================== */

        const role =
          String(
            user.role || ""
          ).trim();



        /* ======================================================
           VALID ROLES
        ====================================================== */

        const allowedRoles = [
          "Doctor",
          "Admin",
          "Reception",
          "Nursing Station",
        ];


        if (
          !allowedRoles.includes(role)
        ) {

          clearLoginData();


          await Swal.fire({
            icon: "error",
            title: "Access Denied",
            text:
              "Your account does not have permission to access this system.",
            confirmButtonText: "OK",
          });


          return;

        }



        /* ======================================================
           USER ID REQUIRED
        ====================================================== */

        if (!user.id) {

          clearLoginData();


          await Swal.fire({
            icon: "error",
            title: "Login Failed",
            text:
              "Invalid user information returned by server.",
          });


          return;

        }



        /* ======================================================
           USERNAME REQUIRED
        ====================================================== */

        const username =
          String(
            user.username ||
            formData.username ||
            ""
          ).trim();


        if (!username) {

          clearLoginData();


          await Swal.fire({
            icon: "error",
            title: "Login Failed",
            text:
              "Username information was not returned by server.",
          });


          return;

        }



        /* ======================================================
           NORMALIZED USER
        ====================================================== */

        const loggedUser = {

          ...user,

          role,

          username,

        };



        /* ======================================================
           CLEAR PREVIOUS SESSION FIRST
        ====================================================== */

        clearLoginData();



        /* ======================================================
           SAVE LOGIN
        ====================================================== */

        localStorage.setItem("accessToken", result.token);
        localStorage.setItem("tokenExpiresAt", String(result.expires_at || ""));

        localStorage.setItem(
          "auth",
          "loggedin"
        );


        localStorage.setItem(
          "user",
          JSON.stringify(
            loggedUser
          )
        );


        localStorage.setItem(
          "userId",
          String(user.id)
        );


        localStorage.setItem(
          "userName",
          String(
            user.name || ""
          )
        );


        localStorage.setItem(
          "username",
          username
        );


        localStorage.setItem(
          "userRole",
          role
        );


        localStorage.setItem(
          "userPhone",
          String(
            user.phone || ""
          )
        );


        localStorage.setItem(
          "userEmail",
          String(
            user.email || ""
          )
        );


        localStorage.setItem(
          "profilePicture",
          String(
            user.profile_picture_url ||
            user.profile_picture ||
            ""
          )
        );


        /*
          ProtectedRoute uses this
          to check session expiry.
        */

        localStorage.setItem(
          "loginTime",
          String(
            Date.now()
          )
        );



        /* ======================================================
           SAME TAB AUTH UPDATE
        ====================================================== */

        /*
          Broadcast successful login to other tabs.

          "storage" events do not fire in the same tab, therefore:
          - loginEvent updates OTHER tabs
          - authChanged updates THIS tab
        */

        localStorage.setItem(
          "loginEvent",
          String(Date.now())
        );

        window.dispatchEvent(
          new Event(
            "authChanged"
          )
        );



        /* ======================================================
           SUCCESS
        ====================================================== */

        await Swal.fire({

          icon: "success",

          title:
            "Login Successful",

          text:
            `Welcome ${
              user.name || ""
            }`,

          timer: 1000,

          showConfirmButton:
            false,

        });



        /* ======================================================
           ROLE REDIRECT
        ====================================================== */

        const normalizedRole =
          role.toLowerCase();



        /* DOCTOR */

        if (
          normalizedRole ===
          "doctor"
        ) {

          navigate(
            "/doctor",
            {
              replace: true,
            }
          );

          return;

        }



        /* ADMIN */

        if (
          normalizedRole ===
          "admin"
        ) {

          navigate(
            "/dashboard",
            {
              replace: true,
            }
          );

          return;

        }



        /* RECEPTION */

        if (
          normalizedRole ===
          "reception"
        ) {

          navigate(
            "/frontdesk",
            {
              replace: true,
            }
          );

          return;

        }



        /* NURSING */

        if (
          normalizedRole ===
          "nursing station"
        ) {

          navigate(
            "/frontdesk",
            {
              replace: true,
            }
          );

          return;

        }



        /* SAFETY FALLBACK */

        clearLoginData();


        navigate(
          "/login",
          {
            replace: true,
          }
        );


        return;

      }



      /* ========================================================
         LOGIN FAILED
      ======================================================== */

      clearLoginData();


      await Swal.fire({

        icon: "error",

        title: "Login Failed",

        text:
          result.message ||
          result.msg ||
          "Invalid username or password.",

      });


    } catch (error) {

      console.error(
        "Login error:",
        error
      );


      clearLoginData();


      await Swal.fire({

        icon: "error",

        title:
          "Connection Error",

        text:
          "Unable to connect to the server. Please try again.",

      });


    } finally {

      setLoading(false);

    }

  };



  /* ============================================================
     UI
  ============================================================ */

  return (

    <div className="login-page">

      <div className="login-wrapper">


        {/* ======================================================
            LEFT SIDE
        ====================================================== */}

        <div className="login-brand-section">

          <div className="login-brand-content">


            <div className="login-brand-icon">

              <i className="fa fa-hospital-o"></i>

            </div>


            <h1
              style={{
                color: "white",
              }}
            >
              Kolath Medical Centre
            </h1>


            <p
              style={{
                color: "white",
              }}
            >
              Clinic Management System
            </p>



            <div className="login-role-list">


              <div>

                <i className="fa fa-user-md"></i>

                <span>
                  Doctor
                </span>

              </div>



              <div>

                <i className="fa fa-user"></i>

                <span>
                  Reception
                </span>

              </div>



              <div>

                <i className="fa fa-medkit"></i>

                <span>
                  Nursing Station
                </span>

              </div>


            </div>

          </div>

        </div>



        {/* ======================================================
            LOGIN SECTION
        ====================================================== */}

        <div className="login-form-section">

          <div className="login-form-box">


            {/* MOBILE LOGO */}

            <div className="login-mobile-logo">

              <i className="fa fa-hospital-o"></i>

            </div>



            {/* HEADING */}

            <div className="login-heading">

              <h2>
                Welcome Back
              </h2>

              <p>
                Sign in to continue to your account
              </p>

            </div>



            {/* ==================================================
                FORM
            ================================================== */}

            <form
              onSubmit={
                handleSubmit
              }
            >


              {/* USERNAME */}

              <div className="login-form-group">

                <label>
                  Username
                </label>


                <div className="login-input-wrapper">

                  <i className="fa fa-user"></i>


                  <input
                    type="text"
                    name="username"
                    placeholder="Enter username"
                    value={
                      formData.username
                    }
                    onChange={
                      handleChange
                    }
                    autoComplete="username"
                    disabled={
                      loading
                    }
                    autoFocus
                  />

                </div>

              </div>



              {/* PASSWORD */}

              <div className="login-form-group">

                <label>
                  Password
                </label>


                <div className="login-input-wrapper">

                  <i className="fa fa-lock"></i>


                  <input
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    name="password"
                    placeholder="Enter password"
                    value={
                      formData.password
                    }
                    onChange={
                      handleChange
                    }
                    autoComplete="current-password"
                    disabled={
                      loading
                    }
                  />



                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() =>
                      setShowPassword(
                        (prev) =>
                          !prev
                      )
                    }
                    tabIndex="-1"
                    disabled={
                      loading
                    }
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >

                    <i
                      className={
                        showPassword
                          ? "fa fa-eye-slash"
                          : "fa fa-eye"
                      }
                    ></i>

                  </button>

                </div>

              </div>



              {/* LOGIN BUTTON */}

              <button
                type="submit"
                className="login-button"
                disabled={
                  loading
                }
              >

                {loading ? (

                  <>

                    <i className="fa fa-spinner fa-spin"></i>

                    &nbsp;
                    Signing in...

                  </>

                ) : (

                  <>

                    <i className="fa fa-sign-in"></i>

                    &nbsp;
                    Login

                  </>

                )}

              </button>


            </form>



            {/* FOOTER */}

            <div className="login-footer">

              <p>
                Authorized staff access only
              </p>

            </div>


          </div>

        </div>

      </div>

    </div>

  );

}